import { AppBackground, Button, Input, Text, ResponsiveLogo, Drop, SelectableItemProps, BottomActionContainer } from '@ui/components'
import React, { useEffect, useMemo, useRef, useState } from "react"
import { Image, KeyboardAvoidingView, Platform, View } from "react-native"
import { ButtonContent, DatePickerWrapper, DisabledButton, DisabledButtonText, InputContainer, InputWrapper, MainContainer, ModalContainer, ModalHeader, ModalWrapper, PreviewButtonContainer } from "@/aena/styles"
import { ReactComponent as ChevronDownIcon } from '@/assets/icons/aena/aena_chevron_down.svg'
import { loadPetChipImage, loadPetPassportImage, useAppDispatch, useAppSelector } from '@front/store'
import { usePetDocumentation, useProfile } from '@front/logic'
import { useNavigate } from "react-router-dom"
import { CamaraCard, ImageTypeSelector } from "@/components/cameracard"
import DatePicker from 'react-datepicker'
import { getDatePickerLocale } from '@/utils/datepickerLocale'
// import "react-datepicker/dist/react-datepicker.css"
import { HeaderWithBackAena } from '@/components/headerwithback'
import { t } from 'i18next'
import { colors, ErrorText, Headline5 } from '@/aena/styles'
import { ResponseGetCountries, updatePetChipPhotoAPI, updatePetDocumentDetailsAPI, updatePetDocumentPhotoAPI, updatePetMicrochipAPI, updateUserAPI } from '@front/apis'
import { SwitchSelector } from '@/components/switchselector'
import { revalidateLogic, useForm, type AnyFieldApi } from '@tanstack/react-form'
import { z } from 'zod'
import Toast from 'react-native-toast-message'

function FieldInfo({ field }: { field: AnyFieldApi }) {
  return (
    <>
      {field.state.meta.isTouched && !field.state.meta.isValid && field.state.meta.errors[0] && (
        <ErrorText>{field.state.meta.errors[0].message}</ErrorText>
      )}
    </>
  )
}

// Etiquetas legibles de los campos OBLIGATORIOS, para el resumen "Faltan: ...".
// Reutilizan exactamente las mismas claves i18n (con su fallback) que ya usa el formulario.
const getRequiredFieldLabel = (key: string): string => {
  switch (key) {
    case 'chipNumber':
      return t('common.chipnumber')
    case 'passportNumber':
      return t('common.passportnumber')
    case 'passportExpirationDate':
      return t('common.passportexpirationdate')
    case 'name':
      return t('editProfile.name', 'Nombre')
    case 'lastName':
      return t('editProfile.lastnames', 'Apellidos')
    case 'phone':
      return t('editProfile.phone', 'Teléfono')
    case 'email':
      return 'Email'
    case 'documentId':
      return t('missingData.dni', 'DNI o NIE')
    case 'address':
      return t('editProfile.address', 'Dirección')
    case 'addressNumber':
      return t('editProfile.addressNumber', 'Nº')
    case 'locality':
      return t('editProfile.locality', 'Localidad')
    case 'province':
      return t('editProfile.province', 'Provincia')
    case 'zipCode':
      return t('editProfile.zipCode', 'Código postal')
    case 'country':
      return t('addPetAdress.country', 'País')
    default:
      return key
  }
}

// Preview INLINE y evidente de la imagen ya subida (chip/pasaporte).
// El usuario reportaba que la foto subida "no se veia bien": con la CamaraCard
// la imagen quedaba debajo del overlay (icono + textos) y apenas se apreciaba.
// Aqui mostramos la miniatura de forma clara, debajo de la tarjeta de subida.
const UploadedImagePreview: React.FC<{ uri?: string }> = ({ uri }) => {
  if (!uri) return null
  return (
    <View style={{ marginTop: 8, gap: 6 }}>
      <Headline5>{t('common.preview', 'Vista previa')}</Headline5>
      <View
        style={{
          borderWidth: 1,
          borderColor: colors.grisMedio,
          borderRadius: 10,
          overflow: 'hidden',
          backgroundColor: colors.grisClaro,
        }}
      >
        <Image
          source={{ uri }}
          resizeMode="contain"
          style={{ width: '100%', height: 200 }}
        />
      </View>
    </View>
  )
}

// Campos obligatorios comunes (sección "Información del propietario").
const COMMON_REQUIRED_FIELDS = [
  'name',
  'lastName',
  'phone',
  'email',
  'documentId',
  'address',
  'addressNumber',
  'locality',
  'province',
  'zipCode',
  'country',
]

// Devuelve la lista ordenada de campos obligatorios que faltan/son inválidos,
// respetando el modo (chip vs pasaporte). Se apoya en form.getAllErrors().
const getMissingRequiredFields = (form: any): string[] => {
  const isChip = form.state.values.isChip
  const requiredKeys = [
    ...(isChip ? ['chipNumber'] : ['passportNumber', 'passportExpirationDate']),
    ...COMMON_REQUIRED_FIELDS,
  ]
  const fieldErrors = form.getAllErrors().fields
  return requiredKeys.filter((key) => {
    const errs = fieldErrors?.[key]?.errors
    return Array.isArray(errs) && errs.length > 0
  })
}


const defaultValues = {
  isChip: true,
  chipUrl: undefined as string | undefined,
  newChipUrl: null as File | null,
  chipNumber: '',
  passportUrl: undefined as string | undefined,
  newPassportUrl: null as File | null,
  passportNumber: '',
  passportExpirationDate: null as Date | null,
  name: '',
  lastName: '',
  documentId: '',
  phone: '',
  email: '',
  roadType: undefined as "CL" | "CAM" | "PLZ" | "AV" | "BLV" | "ROAD" | undefined,
  address: '',
  addressNumber: '',
  addressInfo: '',
  locality: '',
  province: '',
  zipCode: '',
  country: undefined as string | undefined,
  shareEmail: true,
  sharePhone: true,
  shareAddress: true,
}



// mode: 'national' = Documentación nacional (cartilla veterinaria = chip/microchip);
//       'exterior' = Pasaporte (opcional) = pasaporte europeo. Ya NO hay tabs: el modo
// fija isChip y oculta el SwitchSelector.
const Documents: React.FC<{ mode?: 'national' | 'exterior' }> = ({ mode = 'national' }) => {
  const isNationalMode = mode === 'national'
  const validationSchema = z.object({
    isChip: z.boolean(),
    chipUrl: z.string().nullable(),
    newChipUrl: z.instanceof(File).nullable(),
    chipNumber: z.string(),
    passportUrl: z.string().nullable(),
    newPassportUrl: z.instanceof(File).nullable(),
    passportNumber: z.string(),
    passportExpirationDate: z.date().nullable(),
    name: z.string().min(3, t("errors.name_required")),
    lastName: z.string().min(3, t("errors.last_name_required")),
    documentId: z.string(t("missingData.dni_required")).regex(/^([0-9]{8}[A-Z]|[XYZ][0-9]{7}[A-Z])$/, t('missingData.dni_required')),
    phone: z.string(t("editProfile.required_phone")).regex(/^\d{9}$/, t('editProfile.required_phone')),
    email: z.email(t('editProfile.required_email')),
    roadType: z.enum(["CL", "CAM", "PLZ", "AV", "BLV", "ROAD"], {
      error: t('missingData.roadType_required'),
    }),
    address: z.string().min(5, t("editProfile.required_address")),
    addressNumber: z.string(t("missingData.addressNumber_required")).regex(/^\d{1,5}$/, t('missingData.addressNumber_required')),
    addressInfo: z.string(t("missingData.addressInfo_required")),
    locality: z.string().min(3, t("missingData.locality_required")),
    zipCode: z.string().min(1, t("missingData.zipCode_required")).regex(/^\d{5}$/, t('missingData.zipCode_required')),
    province: z.string().min(3, t("missingData.province_required")),
    country: z.string(t("missingData.country_required")),
    shareEmail: z.boolean(),
    sharePhone: z.boolean(),
    shareAddress: z.boolean(),
  }).superRefine((data, ctx) => {
    if (data.isChip) {
      if (!data.chipNumber.match(/^\d{15}$/)) {
        ctx.addIssue({
          path: ['chipNumber'],
          field: 'chipNumber',
          code: "custom",
          message: t('errors.chip_invalid'),
        })
      }
      // if (!data.chipUrl && !data.newChipUrl) {
      //   ctx.addIssue({
      //     field: 'newChipUrl',
      //     code: "custom",
      //     message: t('errors.chip_url_required'),
      //   })
      // }
    } else {
      if (!data.passportNumber.match(/^[A-Z]{2}[A-Z0-9]{6,10}$/)) {
        ctx.addIssue({
          path: ['passportNumber'],
          field: 'passportNumber',
          code: "custom",
          message: t('errors.passport_invalid'),
        })
      }
      // if (data.passportUrl==="" && !data.newPassportUrl) {
      //   ctx.addIssue({
      //     path: ['newPassportUrl'],
      //     field: 'newPassportUrl',
      //     code: "custom",
      //     message: t('errors.passport_url_invalid'),
      //   })
      // }
      if (!data.passportExpirationDate) {
        ctx.addIssue({
          path: ['passportExpirationDate'],
          field: 'passportExpirationDate',
          code: "custom",
          message: t('errors.passport_expiration_date_invalid'),
        })
      }
    }
  })
  
  
  const ROAD_TYPE = [
    { id: "CL", title: t('address.roadType.cl'), data: "CL"},
    { id: "CAM", title: t('address.roadType.cam'), data: "CAM" },
    { id: "PLZ", title: t('address.roadType.plz'), data: "PLZ" },
    { id: "AV", title: t('address.roadType.av'), data: "AV" },
    { id: "BLV", title: t('address.roadType.blv'), data: "BLV" },
    { id: "ROAD", title: t('address.roadType.road'), data: "ROAD" },
  ]  
  const chipUrl = useAppSelector((state) => state.petDocumentation.chip.url)
  const passportUrl = useAppSelector((state) => state.petDocumentation.passport.url)
  const { guid: selectedPetGuid } = useAppSelector((state) => state.selectedPet)

  // const [chipPhoto, setChipPhoto] = useState<string | undefined>(undefined)
  // const [passportPhoto, setPassportPhoto] = useState<string | undefined>(undefined)

  // const [isChip, setIsChip] = React.useState(true)
  // const [imageError, setImageError] = React.useState('')
  // Países con búsqueda (debounce)
  const [countries, setCountries] = useState<ResponseGetCountries[]>([])
  const [countryQuery, setCountryQuery] = useState('')
  const timeoutRef = useRef<NodeJS.Timeout | null>(null)

  const [loading, setLoading] = useState(true)

  const navigate = useNavigate()
  const dispatch = useAppDispatch()

  const {
    values,
    getPetDocumentDetails,
    getPetChipDetails,
    handleSubmit,
  } = usePetDocumentation()

  const { values: valuesProfile, getCountries, handleSubmit: handleSubmitProfile, getProfileContactInfo } = useProfile()
  
  const fetchCountries = async (q = '') => {
    const res = await getCountries({ search: q })
    setCountries(res)
  }

  const onSwitch = async () => {
    const newIsChip = !form.state.values.isChip
    form.setFieldValue('isChip', newIsChip)
    form.state.fieldMeta.isChip.isTouched = true
    form.validate('change')

    // form.validate('change')
    // if (newIsChip) {
    //   // console.log('validate newChipUrl')
    //   // form.validateArrayFieldsStartingFrom('newChipUrl', 'dynamic', true)
    //   form.validateField('passportNumber', 'change')
    //   await form.validateField("newChipUrl", 'change') //.meta.setFieldMeta('newChipUrl', { errorMap: {}, errorSourceMap: {}, isValidating: false })
    // } else {
    //   console.log('validate newPassportUrl')
    //   form.validateField('passportNumber', 'change')

    //   await form.validateField("newPassportUrl", 'change') //.meta.setFieldMeta('newPassportUrl', { errorMap: {}, errorSourceMap: {}, isValidating: false })
    // }
    // setIsChip((prev) => !prev)
  }


  useEffect(() => {
    if (countries.length === 0) {
      fetchCountries('')
    }
    const fetchData = async () => {
      await getPetDocumentDetails()
      await getProfileContactInfo()
      await getPetChipDetails()
      setLoading(false)
    }
    fetchData()
  }, [])

  useEffect(() => {
    if (countryQuery === '') {
      return
    }
    if (timeoutRef.current) clearTimeout(timeoutRef.current)
    timeoutRef.current = setTimeout(() => fetchCountries(countryQuery), 600)
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current)
    }
  }, [countryQuery])
  

  const form = useForm({
    defaultValues: {
      ...defaultValues,
      isChip: isNationalMode,
      passportNumber: values.passportNumber,
      passportExpirationDate: values.passportExpirationDate,
      passportUrl: values.passportUrl,
      chipNumber: values.chipNumber,
      chipUrl: values.chipUrl,

      name: valuesProfile.name,
      lastName: valuesProfile.lastName,
      phone: valuesProfile.phone,
      email: valuesProfile.email,
      documentId: valuesProfile.documentId,

      roadType: valuesProfile.roadType,
      address: valuesProfile.address,
      addressNumber: valuesProfile.addressNumber,
      addressInfo: valuesProfile.addressInfo,
      country: valuesProfile.country,
      zipCode: valuesProfile.zipCode,
      locality: valuesProfile.locality,
      province: valuesProfile.province,
      // shareEmail: valuesProfile.shareEmail,
      // sharePhone: valuesProfile.sharePhone,
      // shareAddress: valuesProfile.shareAddress,
    },
    onSubmit: async ({ value, formApi }) => {
      console.log('value', value)
      const metas = formApi.state.fieldMeta
      // const isDirty = metas.passportNumber.isDirty
      try {
      switch(value.isChip) {
        case true:
          if (metas.chipNumber.isDirty) {
          // UPDATE CHIP
            const resChip = await updatePetMicrochipAPI({
              guid: selectedPetGuid,
              payload: {
                microchip: value.chipNumber,
                legalGuardianDocumentId: value.documentId,
                legalGuardianFullName: value.name + ' ' + value.lastName,
              },
            })
            if (!resChip.success) {
              // errors.push(res.message || 'Error')
              console.error('Error updating chip', resChip.message)
            }
          }
            if (value.newChipUrl) {
            const resChipPhoto = await updatePetChipPhotoAPI({
              guid: selectedPetGuid,
              payload: {
                file: value.newChipUrl,
              },
            })
            console.debug('ChipUrl passed to res', resChipPhoto)
            if (!resChipPhoto.success) {
              // errors.push(res.message || 'Error')
              console.error('Error updating chip photo', resChipPhoto.message)
            }
          }
          
          break
        case false:
          // UPDATE PASSPORRT
          if (metas.passportNumber.isDirty) {
            const resPassport = await updatePetDocumentDetailsAPI({
              guid: selectedPetGuid,
              payload: {
                documentId: value.passportNumber,
                expirationDate: value.passportExpirationDate.toISOString(),
            },
          })
          if (!resPassport.success) {
            // errors.push(res.message || 'Error')
            console.error('Error updating passport', resPassport.message)
          }
          }
        // UPATE PASSPORT PHOTO SOLO SI ES FILE
          if (value.newPassportUrl) {
          console.debug(
            'PassportUrl passed to updatePetDocumentPhotoAPI',
            value.newPassportUrl,
            typeof value.newPassportUrl,
          )
          const resPassportPhoto = await updatePetDocumentPhotoAPI({
            guid: selectedPetGuid,
            payload: {
              file: value.newPassportUrl,
            },
          })
          console.debug('PassportUrl passed to res', resPassportPhoto)
          if (!resPassportPhoto.success) {
              // errors.push(res.message || 'Error')
              console.error('Error updating passport photo', resPassportPhoto.message)
            }
          }
        break
      } 
      if (metas.name.isDirty || metas.lastName.isDirty || metas.phone.isDirty || metas.email.isDirty || metas.documentId.isDirty || metas.address.isDirty || metas.addressInfo.isDirty || metas.zipCode.isDirty || metas.locality.isDirty || metas.province.isDirty || metas.country.isDirty || metas.addressNumber.isDirty || metas.shareEmail.isDirty || metas.sharePhone.isDirty || metas.shareAddress.isDirty) {
      const resProfile = await updateUserAPI({
        name: value.name,
        lastName: value.lastName,
        phone: value.phone,
        email: value.email,
        documentId: value.documentId,
        // roadType: value.roadType,
        address: value.address,
        addressInfo: value.addressInfo,
        zipCode: value.zipCode,
        locality: value.locality,
        province: value.province,
        country: value.country,
        addressNumber: value.addressNumber,
        shareEmail: value.shareEmail,
        sharePhone: value.sharePhone,
        shareAddress: value.shareAddress
      })
      if (!resProfile.success) {
        // errors.push(res.message || 'Error')
        console.error('Error updating profile', resProfile.message)
        }
      }
    } catch (error: any) {
      Toast.show({
        text1: 'Error',
        text2: error.message || 'Error',
        type: 'error',
      })
    }
      // handleSubmit()
      // handleSubmitProfile()
      navigate(-1)

    },
    // validationLogic: revalidateLogic(),
    validators: {
      // onBlur: validationSchema,
      // onMount: validationSchema,
      // onSubmit: validationSchema,
      onChange: validationSchema,
      // onDynamic: validationSchema,
    }
  })

  // Marca como "touched" todos los campos obligatorios (segun el modo) para que
  // FieldInfo muestre el error inline de cada campo vacio, y revalida.
  const markAllRequiredTouched = () => {
    const isChip = form.state.values.isChip
    const requiredKeys = [
      ...(isChip ? ['chipNumber'] : ['passportNumber', 'passportExpirationDate']),
      ...COMMON_REQUIRED_FIELDS,
    ]
    requiredKeys.forEach((key) => {
      form.setFieldMeta(key as any, (meta: any) => ({ ...meta, isTouched: true }))
    })
    form.validateAllFields('submit')
  }

  // Requisito UX #3: en cuanto el usuario EMPIEZA a rellenar (algun campo se
  // vuelve dirty), marcamos todos los obligatorios como touched para que los que
  // siguen vacios se indiquen como faltantes sin esperar a tocar cada uno.
  const startedRef = useRef(false)
  useEffect(() => {
    const unsubscribe = form.store.subscribe(() => {
      if (startedRef.current) return
      if (form.state.isDirty) {
        startedRef.current = true
        markAllRequiredTouched()
      }
    })
    return () => unsubscribe()
  }, [])

  // Intento de envio: si el formulario es invalido NO se envia; en su lugar se
  // marcan todos los obligatorios como touched (errores inline) y se muestra el
  // resumen "Faltan: ...". Si es valido, se ejecuta el guardado normal.
  const onSubmitPress = () => {
    if (!form.state.canSubmit) {
      markAllRequiredTouched()
      return
    }
    form.handleSubmit()
  }

  const onGalleryPress = (file: File, preview: string, type: ImageTypeSelector) => {
    if (type === ImageTypeSelector.CHIP) {
      form.setFieldValue('newChipUrl', file)
      form.setFieldMeta('newChipUrl', { isTouched: true, isBlurred: true, isDirty: true, errorMap: {}, errorSourceMap: {}, isValidating: false })
      form.validate('change')
      dispatch(loadPetChipImage(preview))
    } else {
      form.setFieldValue('newPassportUrl', file)
      form.setFieldMeta('newPassportUrl', { isTouched: true, isBlurred: true, isDirty: true, errorMap: {}, errorSourceMap: {}, isValidating: false })
      form.validate('change')
      dispatch(loadPetPassportImage(preview))
    }
  }
  

  const dropCountries = useMemo(() => {
    return countries.map((c) => ({
      title: c.country,
      id: c.id,
      data: {},
      isSelected: form.state.values.country === c.id,
    }))
  }, [countries, form.state.values.country])


  if (loading) {
    return (
      <Headline5>Cargando...</Headline5>
    )
  }
  return (
    <AppBackground isSafe>
      <View style={{ maxWidth: 800, width: '100%', alignSelf: 'center' }}>
        <View style={{ flexDirection: 'row', justifyContent: 'center', alignItems: 'center', padding: 16, gap: 16 }}>
          <ResponsiveLogo
            breakpoint={820}
            hysteresis={20}
            large={{ width: 680, height: 96 }}
            small={{ width: 360, height: 48 }}
          />
        </View>
        <HeaderWithBackAena title={isNationalMode ? 'Documentación nacional' : t('common.passport')} />
        <form
            onSubmit={(e) => {
              e.preventDefault()
              e.stopPropagation()
              // Mismo comportamiento que el boton: si es invalido NO envia y
              // muestra feedback (errores inline + resumen de lo que falta).
              onSubmitPress()
          }}
          >
        <BottomActionContainer>
        <KeyboardAvoidingView
          behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
          style={{ flex: 1, padding: 10 }}
          keyboardVerticalOffset={Platform.OS === 'ios' ? 10 : 40}
        >
            <MainContainer>
              <form.Subscribe selector={(state) => state.values.isChip}>
                {(isChip) => {
                  return <>
            {/* Sin tabs: el modo (national/exterior) fija la seccion mostrada. */}
                   {isChip ?
                  <React.Fragment key="chip">
                  <InputWrapper>
                    <Headline5>{t('common.photo')}</Headline5>              
                    <InputContainer>
                    <form.Field name="chipUrl">
                    {(field) => <>                   
                      <CamaraCard
                        title={t('common.choosephoto')}
                        subtitle={t('common.uploadchip')}
                        onGalleryPress={onGalleryPress}
                        url={chipUrl}
                        type={ImageTypeSelector.CHIP}
                      />
                      <UploadedImagePreview uri={chipUrl} />
                      <FieldInfo field={field} />
                    </>}
                    </form.Field>
                    </InputContainer>
                  </InputWrapper>
    
                  <InputWrapper>
                    <Headline5>{t('common.chipnumber')} *</Headline5>
                    <InputContainer>
                    <form.Field name="chipNumber">
                      {(field) => (
                        <>
                            <Input
                              inputProps={{
                                value: field.state.value,
                                onChangeText: field.handleChange,
                                onBlur: field.handleBlur,
                                placeholder: t('common.chipnumber'),
                              }}
                            />
                            <FieldInfo field={field} />
                    </>              
                          )}
                    </form.Field>
                    </InputContainer>
                  </InputWrapper>
                  </React.Fragment>
                  :
                  <React.Fragment key="passport">
                  <InputWrapper>
                  <Headline5>{t('common.photo')}</Headline5>
                  <InputContainer>
                  <form.Field name="passportUrl">
                  {(field) => <>                   
                  <CamaraCard
                    title={t('common.choosephoto')}
                    subtitle={t('common.uploadpassport')}
                    onGalleryPress={onGalleryPress}
                    url={passportUrl}
                    type={ImageTypeSelector.PASSPORT}
                  />
                  <UploadedImagePreview uri={passportUrl} />
                  <FieldInfo field={field} />
                  </>}
                  </form.Field>
                  </InputContainer>
                  </InputWrapper>
                  <InputWrapper>
                    <Headline5>{t('common.passportnumber')} *</Headline5>
                    <InputContainer>
                  <form.Field name="passportNumber" 
                  // validators={{
                  //   onChange: ({value}) => {return !isChip && value.length < 3  ? {message: t('errors.passport_invalid')} : undefined},
                  //   onBlur: ({value}) => {return !isChip && value.length < 3  ? {message: t('errors.passport_invalid')} : undefined}
                  // }}
                  >
                    {(field) => (
                      <>
                      <Input
                        inputProps={{
                          value: field.state.value,
                          onChangeText: field.handleChange,
                          onBlur: field.handleBlur,
                          placeholder: t('common.passportnumber'),
                        }}
                      />
                      <FieldInfo field={field} />
                      </>
                    )}
                  </form.Field>
                  </InputContainer>
                  </InputWrapper>
                    <InputWrapper >
                    <InputContainer >
                    <Headline5>{t('common.passportexpirationdate')} *</Headline5>
                    <form.Field name="passportExpirationDate">
                      {(field) => (
                        <>
                        <DatePickerWrapper>
                          <DatePicker
                            portalId="root-portal"
                            selected={field.state.value}
                            date={field.state.value}
                            onChange={field.handleChange}
                            onBlur={field.handleBlur}
                            dateFormat="dd/MM/yyyy"
                            minDate={new Date()}
                            placeholderText={t('common.passportexpirationdate')}
                            className="datepicker-styled"
                            popperPlacement="bottom-start"
                            dropdownMode="select"
                            // maxDate={new Date()}
                            locale={getDatePickerLocale()}
                            showMonthDropdown
                            showYearDropdown
                          />
                        </DatePickerWrapper>
                         <FieldInfo field={field} />
                      </>
                    )}
                  </form.Field>
                    </InputContainer>                
                    </InputWrapper>                  
                  </React.Fragment>
                  }
                  </>
                }}
              </form.Subscribe>
              {/* {isChip ? 
<>chip</>
              : 
<>passport</>
            } */}
              
              <Text type="H4" color="foreground0" fontWeight="700" style={{ marginTop: 20 }} textAlign="center">{t('common.ownerinfo', 'Información del dueño')}</Text>
              <View>
              {/* Nombre */}
              <View style={{ flexDirection: 'row', gap: 16 }}>
                <View style={{ flex: 1 }}>
                <form.Field name="name">
                  {(field) => (
                    <>
                      <InputWrapper>
                        <Headline5>{t('editProfile.name', 'Nombre')} *</Headline5>
                        <InputContainer>
                          <Input
                            inputProps={{
                              value: field.state.value,
                              onChangeText: field.handleChange,
                              onBlur: field.handleBlur,
                              placeholder: t('editProfile.names_placeholder', 'Nombre'),
                            }}
                          />
                          <FieldInfo field={field} />
                        </InputContainer>
                      </InputWrapper>
                    </>
                  )}
                </form.Field>
                </View>
                <View style={{ flex: 1 }}>
                <form.Field name="lastName">
                  {(field) => (
                    <>
                      <InputWrapper>
                        <Headline5>{t('editProfile.lastnames', 'Apellidos')} *</Headline5>
                        <InputContainer>
                          <Input
                            inputProps={{
                              value: field.state.value,
                              onChangeText: field.handleChange,
                              onBlur: field.handleBlur,
                              placeholder: t('editProfile.lastnames_placeholder', 'Apellidos'),
                            }}
                          />
                          <FieldInfo field={field} />
                        </InputContainer>
                      </InputWrapper>
                    </>
                  )}
                </form.Field>
                </View>
                </View>


              {/* Teléfono / Email */}
              <View style={{ flexDirection: 'row', gap: 16 }}>
                <View style={{ flex: 1 }}>
                <form.Field name="phone">
                  {(field) => (
                    <>
                      <InputWrapper>
                        <Headline5>{t('editProfile.phone', 'Teléfono')} *</Headline5>
                        <InputContainer>
                          <Input
                            inputProps={{
                              value: field.state.value,
                              onChangeText: field.handleChange,
                              onBlur: field.handleBlur,
                              placeholder: t('editProfile.phone_placeholder', 'Teléfono'),
                              keyboardType: 'phone-pad',
                            }}
                          />
                          <FieldInfo field={field} />
                        </InputContainer>
                      </InputWrapper>
                    </>
                  )}
                </form.Field>
                </View>
                <View style={{ flex: 1 }}>
                  <form.Field name="email">
                    {(field) => (
                      <>
                      <InputWrapper>
                        <Headline5>Email *</Headline5>
                        <InputContainer>
                          <Input
                            inputProps={{
                              value: field.state.value,
                              onChangeText: field.handleChange,
                              onBlur: field.handleBlur,
                              placeholder: t('editProfile.email_placeholder', 'email@dominio.com'),
                              autoCapitalize: 'none',
                            }}
                          />
                          <FieldInfo field={field} />
                        </InputContainer>
                      </InputWrapper>
                    </>
                  )}
                </form.Field>
                </View>
              </View>

              <View style={{ flexDirection: 'row' }}>
                <View style={{ flex: 1 }}>
                <form.Field name="documentId">
                  {(field) => (
                    <>
                      <InputWrapper>
                        <Headline5>{t('missingData.dni', 'DNI o NIE')} *</Headline5>
                        <InputContainer>
                          <Input
                            inputProps={{
                              value: field.state.value,
                              onChangeText: (text: string) => field.handleChange(text.toUpperCase()),
                              onBlur: field.handleBlur,
                              placeholder: t('missingData.dni', 'DNI o NIE'),
                            }}
                          />
                          <FieldInfo field={field} />
                        </InputContainer>
                      </InputWrapper>
                    </>
                  )}
                </form.Field>
                </View>
              </View>


              {/* Dirección / Nº / Info */}
              <View style={{ flexDirection: 'row', gap: 16 }}>
              <View style={{ flex: 0.7 }}>
                <form.Field name="roadType">
                  {(field) => (
                    <>
                        <Headline5>{t('editProfile.roadType')}</Headline5>
                        <InputContainer>
                          <Drop
                            variant="outlined"
                            color="aena"
                            icon={<ChevronDownIcon width={22} height={22} />}
                            activeColor="#96ce00"
                            placeholder={t('editProfile.roadType_placeholder', 'Tipo de via')}
                            active={ROAD_TYPE.findIndex((obj) => obj.id === field.state.value)}
                            selectedItem={ROAD_TYPE.findIndex((obj) => obj.id === field.state.value)}
                            data={ROAD_TYPE}
                            onPress={(item) => item ? field.handleChange(item.id as "CL" | "CAM" | "PLZ" | "AV" | "BLV" | "ROAD") : undefined}
                            allowIconAndText
                            buttonText={t('common.continue')}
                          />
                          <FieldInfo field={field} />
                          {/* {field.state.value && <BodyText>{field.state.value}</BodyText>} */}
                        </InputContainer>
                    </>
                  )}
                </form.Field>
                </View>
                <View style={{ flex: 1 }}>
                  <form.Field name="address">
                    {(field) => (
                      <>
                        <Headline5>{t('editProfile.address', 'Dirección')} *</Headline5>
                        <InputContainer>
                          <Input
                            inputProps={{
                              value: field.state.value,
                              onChangeText: field.handleChange,
                              onBlur: field.handleBlur,
                              placeholder: t('editProfile.address_placeholder', 'Calle / Avenida'),
                            }}
                          />
                          <FieldInfo field={field} />
                        </InputContainer>
                      </>
                    )}
                  </form.Field>
                </View>
                <View style={{ flex: 0.3 }}>
                  <form.Field name="addressNumber">
                    {(field) => (
                      <>
                        <Headline5>{t('editProfile.addressNumber', 'Nº')} *</Headline5>
                        <InputContainer>
                          <Input
                            inputProps={{
                              value: field.state.value,
                              onChangeText: field.handleChange,
                              onBlur: field.handleBlur,
                              placeholder: t('editProfile.addressNumber_placeholder', 'Nº'),
                            }}
                          />
                          <FieldInfo field={field} />
                        </InputContainer>
                      </>
                    )}
                  </form.Field>
                </View>
              </View>

              <InputWrapper>
                <Headline5>{t('editProfile.addressInfo', 'Info adicional')}</Headline5>
                <form.Field name="addressInfo">
                  {(field) => (
                    <>
                      <InputContainer>
                        <Input
                          inputProps={{
                            value: field.state.value,
                            onChangeText: field.handleChange,
                            onBlur: field.handleBlur,
                            placeholder: t('editProfile.addressInfo_placeholder', 'Escalera, piso, puerta...'),
                          }}
                        />
                        <FieldInfo field={field} />
                      </InputContainer>
                    </>
                  )}
                </form.Field>
              </InputWrapper>

              {/* País (Drop con búsqueda), CP, Localidad, Provincia */}
              <View style={{ flexDirection: 'row', gap: 16 }}>
                <View style={{ flex: 1 }}>
                  <form.Field name="country">
                    {(field) => (
                      <>
                        <Headline5>{t('addPetAdress.country', 'País')} *</Headline5>
                        <InputContainer>
                          <Drop
                            variant="outlined"
                            color="aena"
                            icon={<ChevronDownIcon width={22} height={22} />}
                            activeColor="#96ce00"
                            placeholder={t('addPetAdress.country', 'País')}
                            active={dropCountries.findIndex((obj) => obj.id === field.state.value)}
                            selectedItem={dropCountries.findIndex((obj) => obj.id === field.state.value)}
                            data={dropCountries}
                            onPress={(item) => item ? field.handleChange(item.id as string) : undefined}
                            buttonText={t('common.continue')}
                            allowIconAndText
                            inputProps={{
                              inputProps: {
                                value: countryQuery,
                                onChangeText: (val: string) => setCountryQuery(val),
                                placeholder: t('addPetAdress.placeholderSearch', 'Buscar país'),
                              },
                            }}
                          />            
                        <FieldInfo field={field} />
                      </InputContainer>
                    </>
                  )}
                </form.Field>
                </View>
                <View style={{ flex: 0.6 }}>
                  <form.Field name="zipCode">
                    {(field) => (
                      <>
                  <Headline5>{t('editProfile.zipCode', 'Código postal')} *</Headline5>
                  <InputContainer>
                    <Input
                      inputProps={{
                        value: field.state.value,
                        onChangeText: field.handleChange,
                        onBlur: field.handleBlur,
                        placeholder: t('editProfile.zipCode', 'CP'),
                      }}
                    />
                    <FieldInfo field={field} />
                  </InputContainer>
                  </>)}
                  </form.Field>
                </View>
              </View>

              <View style={{ flexDirection: 'row', gap: 16 }}>
                <View style={{ flex: 1 }}>
                  <form.Field name="locality">
                    {(field) => (
                      <>
                      <InputWrapper>
                        <Headline5>{t('editProfile.locality', 'Localidad')} *</Headline5>
                        <InputContainer>
                          <Input
                            inputProps={{
                              value: field.state.value,
                              onChangeText: field.handleChange,
                              onBlur: field.handleBlur,
                              placeholder: t('editProfile.locality_placeholder', 'Ciudad / Población'),
                            }}
                          />
                          <FieldInfo field={field} />
                        </InputContainer>
                      </InputWrapper>
                      </>
                    )}
                  </form.Field>
                </View>
                <View style={{ flex: 1 }}>
                  <form.Field name="province">
                    {(field) => (
                      <>
                        <Headline5>{t('editProfile.province', 'Provincia')} *</Headline5>
                        <InputContainer>
                          <Input
                            inputProps={{
                              value: field.state.value,
                              onChangeText: field.handleChange,
                              onBlur: field.handleBlur,
                              placeholder: t('editProfile.province_placeholder', 'Provincia'),
                            }}
                          />
                          <FieldInfo field={field} />
                        </InputContainer>
                      </>
                    )}
                  </form.Field>
                </View>
              </View>
              </View>
            </MainContainer>
            {/* Resumen de campos obligatorios que faltan. Aparece cuando el form
                es invalido y el usuario ya ha empezado a rellenarlo o ha intentado
                enviar (algun campo touched). Lista CADA campo que falta. */}
            <form.Subscribe
              selector={(state) => ({
                canSubmit: state.canSubmit,
                isTouched: state.isTouched,
                isDirty: state.isDirty,
                // dependencias para que recalcule el resumen al cambiar valores/meta
                values: state.values,
                fieldMeta: state.fieldMeta,
              })}
            >
              {({ canSubmit, isTouched, isDirty }) => {
                if (canSubmit) return null
                if (!isTouched && !isDirty) return null
                const missing = getMissingRequiredFields(form).map(getRequiredFieldLabel)
                if (missing.length === 0) return null
                return (
                  <View
                    style={{
                      backgroundColor: '#fdecec',
                      borderColor: '#ff4b4b',
                      borderWidth: 1,
                      borderRadius: 8,
                      padding: 12,
                      marginBottom: 8,
                    }}
                  >
                    <ErrorText style={{ fontSize: 13, marginBottom: 2 }}>
                      {t(
                        'missingData.missing_fields_title',
                        'Faltan campos obligatorios por completar:',
                      )}
                    </ErrorText>
                    <ErrorText style={{ fontSize: 13 }}>
                      {`${t('missingData.missing_fields_prefix', 'Faltan')}: ${missing.join(', ')}`}
                    </ErrorText>
                  </View>
                )
              }}
            </form.Subscribe>
            <form.Subscribe selector={(state) => state.canSubmit}>
            {(canSubmit) => canSubmit ? (
            <Button
              title={t('common.update')}
              buttonWrapperStyle={{ backgroundColor: colors.aenaVerde }}
              size="block"
              textType="cta_medium"
              borderRadius={5}
              textColor="background0"
              color="aena"
              onPress={onSubmitPress}
            />
            ) : (
            // Sigue mostrandose en gris (apariencia "deshabilitada") pero ES
            // pulsable: al pulsar NO se envia, marca todos los obligatorios como
            // touched (errores inline) y muestra el resumen de lo que falta.
            <DisabledButton onPress={onSubmitPress}>
              <ButtonContent>
                <DisabledButtonText>{t('common.update')}</DisabledButtonText>
              </ButtonContent>
            </DisabledButton>
            )}
            </form.Subscribe>
        </KeyboardAvoidingView>
        </BottomActionContainer>
        </form>
      </View>
    </AppBackground>
  );
};

export default Documents;